import React, { createContext, useContext, useEffect, useState } from 'react'
import { SseEventDto } from '@/api/model'
import { fetchLiveActivitiesHistory } from './api'
import { RelativeTime } from './types'

// Simple LRU for dedup by id (preload + SSE)
class SeenLRU {
  private map = new Map<string, true>()
  constructor(private cap = 2000) {}
  has(id: string) {
    return this.map.has(id)
  }
  add(id: string) {
    if (this.map.has(id)) return
    this.map.set(id, true)
    if (this.map.size > this.cap) {
      const k = this.map.keys().next().value
      this.map.delete(k)
    }
  }
}

// Ring queue implementation
class RingQueue<T> {
  private items: T[] = []
  constructor(private cap: number) {}

  push(item: T) {
    this.items.push(item)
    if (this.items.length > this.cap) {
      this.items.shift()
    }
  }

  shift(): T | undefined {
    return this.items.shift()
  }

  isEmpty(): boolean {
    return this.items.length === 0
  }
}

// Constants
const MAX_QUEUE_SIZE = 100
const BASE_MIN_DISPLAY_MS = 2000

// Utility functions
function toMs(timestamp: string | number | Date): number {
  if (typeof timestamp === 'number') return timestamp
  return new Date(timestamp).getTime()
}

export function formatRelative(timestamp: number): RelativeTime {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 60) return { unit: 'seconds', count: seconds }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return { unit: 'minutes', count: minutes }

  const hours = Math.floor(minutes / 60)
  return { unit: 'hours', count: hours }
}

// Types
type Subscriber = (snapshot: {
  current: SseEventDto | null
  relative: RelativeTime | undefined
}) => void

// Store implementation
class LiveActivitiesStore<TItem extends SseEventDto = SseEventDto> {
  private queue = new RingQueue<TItem>(MAX_QUEUE_SIZE)
  private current: TItem | null = null
  private currentTs: number | null = null
  private subs = new Set<Subscriber>()
  private rotateTimer: number | null = null
  private relTimer: number | null = null
  private seen = new SeenLRU(2000)

  enqueue = (item: TItem) => {
    const id = String(item.id)
    if (this.seen.has(id)) return

    this.seen.add(id)
    this.queue.push(item)

    // Если нет current или таймер не запущен (значит зависли на последнем),
    // запускаем ротацию
    if (!this.current || !this.rotateTimer) {
      this.rotateNext()
    }
  }

  enqueueMany = (items: TItem[], asInitial = false) => {
    // Сортируем от новых к старым (reverse order)
    const sorted = [...items].sort(
      (a, b) => toMs(a.data.timestamp || '') - toMs(b.data.timestamp || ''),
    )
    for (const it of sorted) this.enqueue(it)
    if (asInitial && !this.current) this.rotateNext()
  }

  subscribe = (fn: Subscriber): (() => void) => {
    this.subs.add(fn)
    fn({
      current: this.current,
      relative: this.relativeStr(),
    })
    return () => {
      this.subs.delete(fn)
    }
  }

  // ---- internals ----
  private relativeStr() {
    return this.currentTs ? formatRelative(this.currentTs) : undefined
  }

  private notify() {
    const snapshot = {
      current: this.current,
      relative: this.relativeStr(),
    }
    this.subs.forEach((fn) => fn(snapshot))
  }

  private startRelTicker() {
    if (this.relTimer != null) window.clearInterval(this.relTimer)
    if (!this.currentTs) return
    this.relTimer = window.setInterval(
      () => this.notify(),
      1000,
    ) as unknown as number
  }

  private stopRotate() {
    if (this.rotateTimer != null) {
      window.clearTimeout(this.rotateTimer)
      this.rotateTimer = null
    }
  }

  private rotateNext() {
    const next = this.queue.shift()

    if (!next) {
      // Нет новых событий - зависаем на текущем
      // Останавливаем таймер ротации, но оставляем current
      this.stopRotate()
      return
    }

    this.current = next
    this.currentTs = toMs(next.data.timestamp || '')
    this.notify()
    this.startRelTicker()

    const minHold = BASE_MIN_DISPLAY_MS

    this.stopRotate()
    this.rotateTimer = window.setTimeout(() => {
      this.rotateNext()
    }, minHold) as unknown as number
  }
}

// Global store instance
export const liveActivitiesStore = new LiveActivitiesStore()

// Context
type LiveCtxValue = {
  current: SseEventDto | null
  relative: RelativeTime | undefined
  enqueue: (item: SseEventDto) => void
  preload: (items: SseEventDto[]) => void
}

const LiveCtx = createContext<LiveCtxValue | null>(null)

// Provider component
export function LiveActivitiesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [current, setCurrent] = useState<SseEventDto | null>(null)
  const [relative, setRelative] = useState<RelativeTime>()

  useEffect(() => {
    return liveActivitiesStore.subscribe(({ current, relative }) => {
      setCurrent(current)
      setRelative(relative)
    })
  }, [])

  // Load initial history on mount
  useEffect(() => {
    fetchLiveActivitiesHistory(5).then((events) => {
      if (events.length > 0) {
        liveActivitiesStore.enqueueMany(events, true)
      }
    })
  }, [])

  const enqueue = (item: SseEventDto) => liveActivitiesStore.enqueue(item)
  const preload = (items: SseEventDto[]) =>
    liveActivitiesStore.enqueueMany(items, true)

  return (
    <LiveCtx.Provider value={{ current, relative, enqueue, preload }}>
      {children}
    </LiveCtx.Provider>
  )
}

// Hook to use the context
export function useLiveActivities() {
  const ctx = useContext(LiveCtx)
  if (!ctx) {
    throw new Error(
      'useLiveActivities must be used within LiveActivitiesProvider',
    )
  }
  return ctx
}
